// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button"
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../../shared-components/forms/NumberedHeading";
import PEDialogInitialPromptArray from './PEDialogInitialPromptArray';
import PEDialogInitialResponseArray from './PEDialogInitialResponseArray';
import PEDialogCorrectionPromptArray from './PEDialogCorrectionPromptArray';
import PEDialogCorrectionResponseArray from './PEDialogCorrectionResponseArray';
// data
import { ReviewData } from "../../../data/Defaults";

export default function PEDialogSegmentArray({control, errors, inputDir}) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `content.dialog_segments`
  })

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className='py-0'>
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Dialog Segment {index+1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className='p-0'>
                    <Box className='p-0'>
                      <NumberedHeading number='1' title='Initial Prompt' instructions='Add one for each initial dialog you need.' />
                      <Controller 
                        control={control}
                        name={`content.dialog_segments.${index}.initial_prompts`}
                        render={({ field }) => <PEDialogInitialPromptArray nestIndex={index} inputDir={inputDir} {...{control, errors }} />}
                      />

                      <Divider className="mt-16" />
                      
                      <NumberedHeading number='2' title='Initial Responses' instructions='Using the controls below add lures for the initial prompt.' />
                      <Controller 
                        control={control}
                        name={`content.dialog_segments.${index}.initial_responses`}
                        render={({ field }) => <PEDialogInitialResponseArray nestIndex={index} inputDir={inputDir} {...{control, errors }} />}
                      />

                      <Divider className="mt-16" />
                      
                      <NumberedHeading number='3' title='Self Correction Prompt' instructions='Add one for each self correction dialog you need.' />
                      <Controller 
                        control={control}
                        name={`content.dialog_segments.${index}.self_correction_prompt`}
                        render={({ field }) => <PEDialogCorrectionPromptArray nestIndex={index} inputDir={inputDir} {...{control, errors }} />}
                      />

                      <Divider className="mt-16" />
                      
                      <NumberedHeading number='4' title='Self Correction Responses' instructions='Using the controls below add lures for the Self Correction prompt.' />
                      <Controller 
                        control={control}
                        name={`content.dialog_segments.${index}.self_correction_responses`}
                        render={({ field }) => <PEDialogCorrectionResponseArray nestIndex={index} inputDir={inputDir} {...{control, errors }} />}
                      />
                    
                      <Divider className="mt-16" />
                      
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          )
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({
            id: BASEUtils.generateGUID(),
            review: ReviewData,
            initial_prompts: [
              {
                user: "",
                use_audio: false,
                text: "",
              },
            ],
            initial_responses: [
              {
                use_audio: false,
                lure_text: "",
                is_correct: false,
                feedbacks: [
                  {
                    user: "",
                    use_audio: false,
                    text: "",
                  },
                ],
              },
            ],
            self_correction_prompts: [
              {
                user: "",
                use_audio: false,
                text: "",
              },
            ],
            self_correction_responses: [
              {
                use_audio: false,
                lure_text: "",
                is_correct: false,
                feedbacks: [
                  {
                    user: "",
                    use_audio: false,
                    text: "",
                  },
                ],
              },
            ],
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Dialog Segment
          </span>
        </Button>
      </Box>
    </>
  )
}